/**
 * Error boundaries (cards YQnX18mKWGxG + bdsxKQG9HrOQ).
 *
 * React error boundaries must be class components — there is no hook
 * equivalent. Two of them, one job each:
 *
 *   - `ErrorBoundary`   the SHELL. Wraps the whole app in main.tsx, so a crash
 *                       anywhere replaces the page instead of blanking it.
 *   - `PanelBoundary`   a TAB PANEL. Wraps each panel's contents in App.tsx, so
 *                       one broken panel shows the apology INSIDE its own slot
 *                       and the bottom nav, header and every other tab stay
 *                       alive — the shell boundary would have taken all of it.
 *
 * Both report what they render (card bdsxKQG9HrOQ): componentDidCatch posts the
 * same error through lib/clientErrors, which dedupes on message+route so a
 * render loop is one report, not one per frame.
 *
 * The copy is GRACEFUL_FAILURE from lib/errorCopy.ts — the one apology the app
 * owns, already carrying support@pearconnected.io. No sentence is invented
 * here; a second "we're sorry" wording is how the two support addresses
 * happened once already.
 *
 * Colours come from design/tokens.css custom properties (styles in
 * styles/app.css).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { GRACEFUL_FAILURE } from '../lib/errorCopy.ts';
import { reportClientError } from '../lib/clientErrors.ts';

type BoundaryKind = 'boundary' | 'panel';

interface BoundaryProps {
  children: ReactNode;
  /** Which reporter label this boundary files under — 'boundary' for the
   *  shell, 'panel' for a tab panel. */
  kind?: BoundaryKind;
}

interface BoundaryState {
  error: Error | null;
}

/** Shared reporting + render, so the two surfaces cannot drift apart. */
class BaseBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, _info: ErrorInfo): void {
    // console.error first: it is what the browser's own console shows, and it
    // cannot throw.
    console.error('[Pear] render error caught by ErrorBoundary:', error);
    reportClientError(this.props.kind ?? 'boundary', error.message, error.stack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    const panel = (this.props.kind ?? 'boundary') === 'panel';

    return (
      <div
        className={panel ? 'error-boundary error-boundary-panel' : 'error-boundary'}
        role="alert"
      >
        {!panel && <p className="error-boundary-title">Something went sideways.</p>}
        <p className="error-boundary-note">{GRACEFUL_FAILURE}</p>
        <button type="button" className="error-boundary-reload" onClick={this.handleReload}>
          Reload Pear
        </button>
      </div>
    );
  }
}

/** The shell boundary — the one main.tsx has always wrapped the app in. */
export function ErrorBoundary({ children }: { children: ReactNode }): ReactNode {
  return <BaseBoundary kind="boundary">{children}</BaseBoundary>;
}

/** A tab panel's own boundary: same apology, scoped to the panel, so the nav
 *  survives a crash in whichever screen is up. */
export function PanelBoundary({ children }: { children: ReactNode }): ReactNode {
  return <BaseBoundary kind="panel">{children}</BaseBoundary>;
}
